// widgets/BiologicalAgeWidget.tsx
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { User, TrendingDown, Sparkles, Heart, Activity } from "lucide-react";

const BiologicalAgeWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const bioAgeTab = tabs.find((tab) => tab.id === "bioAge");
    const widget = bioAgeTab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white/90 via-violet-50/30 to-purple-100/20 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-violet-200/40 mb-4 hover:shadow-violet-200/30 hover:shadow-2xl transition-all duration-500 group overflow-hidden relative">
                {/* Background effects */}
                <div className={`absolute ${isRTL ? "left-10" : "right-10"} -top-10 w-40 h-40 bg-gradient-to-br from-violet-200 to-purple-100 rounded-full opacity-20 blur-2xl`}></div>
                <div className={`absolute ${isRTL ? "right-10" : "left-10"} -bottom-10 w-40 h-40 bg-gradient-to-br from-purple-200 to-violet-100 rounded-full opacity-20 blur-2xl`}></div>

                {/* Header */}
                <div className="flex justify-between items-center mb-6 relative z-10">
                    <div className="flex items-center">
                        <motion.div
                            className={`me-3 p-3 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl shadow-lg`}
                            animate={{
                                scale: [1, 1.05, 1],
                                rotate: [0, 3, -3, 0],
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <User className="w-6 h-6 text-white" />
                        </motion.div>
                        <div>
                            <h3 className="text-xl font-bold text-violet-900 mb-1">{widget.title}</h3>
                            <p className="text-sm text-violet-600">{widget.subtitle}</p>
                        </div>
                    </div>
                </div>

                {/* Age comparison display */}
                <div className="relative mb-6">
                    <div className="grid grid-cols-2 gap-4">
                        {/* Biological Age */}
                        <motion.div
                            className="relative p-6 bg-white/60 backdrop-blur-md rounded-2xl border border-violet-200/50 shadow-lg overflow-hidden"
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.3, duration: 0.6 }}
                            whileHover={{ scale: 1.02, y: -3 }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-violet-400/10 to-purple-400/10"></div>
                            <div className="relative z-10">
                                <p className="text-sm text-gray-600 mb-2">{widget.biologicalAge?.label}</p>
                                <motion.div
                                    className="text-4xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent"
                                    initial={{ scale: 0.5 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                                >
                                    {widget.biologicalAge?.value}
                                </motion.div>
                                <p className="text-xs text-violet-600 mt-1">{widget.biologicalAge?.unit}</p>
                            </div>
                        </motion.div>

                        {/* Actual Age */}
                        <motion.div
                            className="relative p-6 bg-white/60 backdrop-blur-md rounded-2xl border border-gray-200/50 shadow-lg overflow-hidden"
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                            whileHover={{ scale: 1.02, y: -3 }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-gray-400/10 to-gray-300/10"></div>
                            <div className="relative z-10">
                                <p className="text-sm text-gray-600 mb-2">{widget.actualAge?.label}</p>
                                <div className="text-4xl font-bold text-gray-700">{widget.actualAge?.value}</div>
                                <p className="text-xs text-gray-600 mt-1">{widget.actualAge?.unit}</p>
                            </div>
                        </motion.div>
                    </div>

                    {/* Difference indicator */}
                    <motion.div
                        className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.8 }}
                    >
                        <TrendingDown className="w-4 h-4" />
                        {widget.difference}
                    </motion.div>
                </div>

                {/* Health factors */}
                <div className="space-y-3 mb-6 relative z-10">
                    <h4 className="text-sm font-semibold text-violet-900 mb-3 flex items-center">
                        <Activity className={`w-4 h-4 me-2 text-violet-600`} />
                        {widget.factors?.title}
                    </h4>

                    {widget.factors?.items?.map((factor: any, index: number) => (
                        <motion.div
                            key={index}
                            className="flex items-center justify-between p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-violet-100/50"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 1 + index * 0.1 }}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${factor.color} flex items-center justify-center text-white`}>
                                    {factor.icon === "heart" && <Heart className="w-4 h-4" />}
                                    {factor.icon === "activity" && <Activity className="w-4 h-4" />}
                                    {factor.icon === "sparkles" && <Sparkles className="w-4 h-4" />}
                                </div>
                                <span className="text-sm font-medium text-gray-800">{factor.label}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <motion.div
                                        className={`h-full bg-gradient-to-r ${factor.color}`}
                                        initial={{ width: 0 }}
                                        animate={{ width: `${factor.score}%` }}
                                        transition={{ duration: 1, delay: 1.2 + index * 0.1 }}
                                    />
                                </div>
                                <span className="text-sm font-bold text-violet-700 w-10 text-right">{factor.score}%</span>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* AI Insights */}
            <motion.div
                className="bg-gradient-to-br from-violet-50/60 to-purple-50/40 backdrop-blur-xl rounded-2xl p-4 border border-violet-200/50 shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
            >
                <div className="flex items-center mb-3">
                    <motion.div
                        className={`w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center me-2 shadow-lg`}
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                        <Sparkles className="w-5 h-5 text-white" />
                    </motion.div>
                    <div>
                        <h4 className="font-bold text-violet-900">{widget.insights?.title}</h4>
                        <p className="text-xs text-violet-600">{widget.insights?.subtitle}</p>
                    </div>
                </div>

                <div className="space-y-2">
                    {widget.insights?.recommendations?.map((rec: string, index: number) => (
                        <motion.div
                            key={index}
                            className="flex items-start gap-2 p-2 bg-white/60 backdrop-blur-sm rounded-lg"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.7 + index * 0.1 }}
                        >
                            <div className="w-5 h-5 mt-0.5 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs">✓</span>
                            </div>
                            <p className="text-sm text-gray-700">{rec}</p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        </motion.div>
    );
};

export default BiologicalAgeWidget;
